import { db } from './db';

/**
 * The share links made on this device, with the secret that deletes them.
 *
 * A share made without an account is not listed anywhere on the server — there
 * is no account to list it under — so the only way to take one back is the
 * deletion secret the server hands out once, at creation. Keeping it here is
 * what turns "you cannot delete this" into "delete" in the share list.
 *
 * It lives in IndexedDB rather than in the cloud precisely because it is a
 * capability: it belongs to whoever made the link, not to markbird.
 */

const KEY = 'localShares';
/** Enough to keep a working history without growing forever. */
const LIMIT = 200;

export interface LocalShare {
  /** A share's token, or a published site's id. */
  token: string;
  /** What the link is. Absent on links made before sites existed: a share. */
  kind?: 'share' | 'site';
  url: string;
  title: string;
  createdAt: number;
  expiresAt: number | null;
  deleteToken: string;
  deleteUrl: string;
}

function isLive(share: LocalShare, now: number): boolean {
  return share.expiresAt == null || share.expiresAt > now;
}

/** Links made on this device, newest first. Expired ones are dropped as they age out. */
export async function listLocalShares(now = Date.now()): Promise<LocalShare[]> {
  const stored = ((await db.kv.get(KEY))?.value as LocalShare[] | undefined) ?? [];
  const live = stored.filter((s) => isLive(s, now));
  if (live.length !== stored.length) await db.kv.put({ key: KEY, value: live });
  return [...live].sort((a, b) => b.createdAt - a.createdAt);
}

export async function rememberLocalShare(share: LocalShare): Promise<void> {
  const kept = (await listLocalShares()).filter((s) => s.token !== share.token);
  await db.kv.put({ key: KEY, value: [share, ...kept].slice(0, LIMIT) });
}

export async function forgetLocalShare(token: string): Promise<void> {
  const kept = (await listLocalShares()).filter((s) => s.token !== token);
  await db.kv.put({ key: KEY, value: kept });
}

/**
 * Delete a share with its secret and forget it here. A share the server no
 * longer has (expired, or already deleted) is forgotten too: the aim is that it
 * is gone, and it is.
 *
 * A published site is deleted the same way, at its own endpoint — one secret
 * for the whole site, which takes every page with it.
 */
export async function deleteLocalShare(share: LocalShare): Promise<boolean> {
  let gone: boolean;
  try {
    const path = share.kind === 'site' ? 'sites' : 'shares';
    const res = await fetch(`/api/${path}/${share.token}`, {
      method: 'DELETE',
      headers: { 'x-delete-secret': share.deleteToken },
    });
    gone = res.ok || res.status === 404;
  } catch {
    // Offline: keep the secret, so the link can still be deleted later.
    gone = false;
  }
  if (gone) await forgetLocalShare(share.token);
  return gone;
}
